import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useSessionStore } from '@/stores/session'

// `module` in den Meta-Daten blendet Routen aus, für die der Benutzer nicht
// freigeschaltet ist. Der eigentliche Schutz liegt im Backend (§4) – das hier
// verhindert nur, dass jemand in eine leere Seite läuft.
declare module 'vue-router' {
  interface RouteMeta {
    module?: string
    requiresAdmin?: boolean
    public?: boolean
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/setup',
    name: 'setup',
    component: () => import('@/views/SetupView.vue'),
    meta: { public: true },
  },
  {
    path: '/anmelden',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true },
  },
  {
    path: '/service-aktion/:token',
    name: 'service-action',
    component: () => import('@/views/ServiceActionView.vue'),
    meta: { public: true },
  },
  {
    path: '/sepa-mandat/:token',
    name: 'sepa-mandate-sign',
    component: () => import('@/views/SepaMandateSignView.vue'),
    meta: { public: true },
  },
  {
    path: '/shop/:slug',
    name: 'shop-checkout',
    component: () => import('@/views/ShopCheckoutView.vue'),
    meta: { public: true },
  },
  {
    path: '/portal',
    name: 'portal-login',
    component: () => import('@/views/portal/PortalLoginView.vue'),
    meta: { public: true },
  },
  {
    path: '/portal/einrichten/:token',
    name: 'portal-setup',
    component: () => import('@/views/portal/PortalSetupView.vue'),
    meta: { public: true },
  },
  {
    path: '/portal/app',
    name: 'portal-app',
    component: () => import('@/views/portal/PortalAppView.vue'),
    meta: { public: true },
  },
  {
    path: '/',
    component: () => import('@/layouts/AppShell.vue'),
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/DashboardView.vue'),
        meta: { module: 'dashboard' },
      },
      {
        path: 'angebote',
        name: 'offers',
        component: () => import('@/views/OffersListView.vue'),
        meta: { module: 'offers' },
      },
      {
        path: 'angebote/vorlagen',
        name: 'offer-templates',
        component: () => import('@/views/OfferTemplatesView.vue'),
        meta: { module: 'offers' },
      },
      {
        path: 'angebote/:id',
        name: 'offer-editor',
        component: () => import('@/views/OfferEditorView.vue'),
        meta: { module: 'offers' },
      },
      {
        path: 'auftraege',
        name: 'orders',
        component: () => import('@/views/OrdersListView.vue'),
        meta: { module: 'orders' },
      },
      {
        path: 'stunden',
        name: 'time-tracking',
        component: () => import('@/views/TimeTrackingView.vue'),
        meta: { module: 'time_tracking' },
      },
      {
        path: 'auftraege/:id',
        name: 'order-detail',
        component: () => import('@/views/OrderDetailView.vue'),
        meta: { module: 'orders' },
      },
      {
        path: 'bestellliste',
        name: 'purchase-list',
        component: () => import('@/views/PurchaseListView.vue'),
        meta: { module: 'purchase_list' },
      },
      {
        path: 'wareneingang',
        name: 'goods-receipt',
        component: () => import('@/views/GoodsReceiptView.vue'),
        meta: { module: 'goods_receipt' },
      },
      {
        path: 'lagerbestand',
        name: 'stock',
        component: () => import('@/views/StockView.vue'),
        meta: { module: 'stock' },
      },
      {
        path: 'vertraege',
        name: 'contracts',
        component: () => import('@/views/ContractsView.vue'),
        meta: { module: 'contracts' },
      },
      {
        path: 'paylinks',
        name: 'paylinks',
        component: () => import('@/views/PaylinksView.vue'),
        meta: { module: 'paylinks' },
      },
      {
        path: 'abrechnung',
        name: 'billing',
        component: () => import('@/views/BillingView.vue'),
        meta: { module: 'billing' },
      },
      {
        path: 'rechnungen',
        name: 'invoices',
        component: () => import('@/views/InvoicesListView.vue'),
        meta: { module: 'invoices' },
      },
      {
        path: 'rechnungen/:id',
        name: 'invoice-detail',
        component: () => import('@/views/InvoiceDetailView.vue'),
        meta: { module: 'invoices' },
      },
      {
        path: 'werkstatt',
        name: 'workshop',
        component: () => import('@/views/WorkshopView.vue'),
        meta: { module: 'workshop' },
      },
      {
        path: 'rueckrufe',
        name: 'callbacks',
        component: () => import('@/views/CallbacksView.vue'),
        meta: { module: 'callbacks' },
      },
      {
        path: 'aussendienst',
        name: 'field-service',
        component: () => import('@/views/FieldServiceView.vue'),
        meta: { module: 'field_service' },
      },
      {
        path: 'statistik',
        name: 'statistics',
        component: () => import('@/views/StatisticsView.vue'),
        meta: { module: 'statistics' },
      },
      {
        path: 'offene-posten',
        name: 'receivables',
        component: () => import('@/views/ReceivablesView.vue'),
        meta: { module: 'receivables' },
      },
      {
        path: 'bank-abgleich',
        name: 'bank',
        component: () => import('@/views/BankReconciliationView.vue'),
        meta: { module: 'receivables' },
      },
      {
        path: 'sepa',
        name: 'sepa',
        component: () => import('@/views/SepaView.vue'),
        meta: { module: 'receivables' },
      },
      {
        path: 'einstellungen/firmendaten',
        name: 'settings-company',
        component: () => import('@/views/settings/CompanySettingsView.vue'),
        meta: { module: 'settings', requiresAdmin: true },
      },
      {
        path: 'einstellungen/sepa',
        name: 'settings-sepa',
        component: () => import('@/views/settings/SepaSettingsView.vue'),
        meta: { module: 'settings', requiresAdmin: true },
      },
      {
        path: 'einstellungen/email',
        name: 'settings-mail',
        component: () => import('@/views/settings/MailSettingsView.vue'),
        meta: { module: 'settings', requiresAdmin: true },
      },
      {
        path: 'einstellungen/email-vorlagen',
        name: 'settings-mail-templates',
        component: () => import('@/views/settings/EmailTemplatesView.vue'),
        meta: { module: 'settings', requiresAdmin: true },
      },
      {
        path: 'kunden',
        name: 'customers',
        component: () => import('@/views/CustomersView.vue'),
        meta: { module: 'customers' },
      },
      {
        path: 'portalzugaenge',
        name: 'portal-access',
        component: () => import('@/views/settings/PortalAccessView.vue'),
        meta: { module: 'customer_portal', requiresAdmin: true },
      },
      {
        path: 'dokumentation',
        name: 'docs',
        component: () => import('@/views/DokumentationHubView.vue'),
        meta: { module: 'verfahrensdoku', requiresAdmin: true },
      },
      {
        path: 'dokumentation/verfahren',
        name: 'docs-verfahren',
        component: () => import('@/views/VerfahrensdokumentationView.vue'),
        meta: { module: 'verfahrensdoku', requiresAdmin: true },
      },
      {
        path: 'dokumentation/technik',
        name: 'docs-technik',
        component: () => import('@/views/TechnischeDokumentationView.vue'),
        meta: { module: 'verfahrensdoku', requiresAdmin: true },
      },
      {
        path: 'lieferanten',
        name: 'suppliers',
        component: () => import('@/views/SuppliersView.vue'),
        meta: { module: 'suppliers' },
      },
      {
        path: 'artikel',
        name: 'articles',
        component: () => import('@/views/ArticlesView.vue'),
        meta: { module: 'articles' },
      },
      {
        path: 'einstellungen/benutzer',
        name: 'settings-users',
        component: () => import('@/views/settings/UsersView.vue'),
        meta: { module: 'settings', requiresAdmin: true },
      },
      {
        path: 'einstellungen/versand',
        name: 'settings-shipping',
        component: () => import('@/views/settings/ShippingView.vue'),
        meta: { module: 'settings', requiresAdmin: true },
      },
      {
        path: 'einstellungen/stundensaetze',
        name: 'settings-rates',
        component: () => import('@/views/settings/RatesView.vue'),
        meta: { module: 'settings', requiresAdmin: true },
      },
      {
        path: 'einstellungen/integrationen',
        name: 'settings-integrations',
        component: () => import('@/views/settings/IntegrationsView.vue'),
        meta: { module: 'settings', requiresAdmin: true },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to) => {
  const session = useSessionStore()

  // Beim ersten Aufruf wissen wir noch nichts über Setup-Stand und Anmeldung.
  if (session.setupComplete === null) {
    await session.load()
  }

  if (session.setupComplete === false) {
    return to.name === 'setup' ? true : { name: 'setup' }
  }

  // Eine eingerichtete Instanz hat im Setup-Wizard nichts mehr verloren.
  if (to.name === 'setup') return { name: 'dashboard' }

  if (to.meta.public) {
    return session.isAuthenticated && to.name === 'login' ? { name: 'dashboard' } : true
  }

  if (!session.isAuthenticated) {
    return { name: 'login', query: { returnTo: to.fullPath } }
  }

  if (to.meta.requiresAdmin && !session.isAdmin) {
    return { name: 'dashboard' }
  }

  if (to.meta.module && !session.hasModule(to.meta.module)) {
    return { name: 'dashboard' }
  }

  return true
})
